import {
  SearchableSelect,
  type SearchableSelectItem,
} from "@/components/molecules/searchable-select";
import { UserAvatar } from "@/components/molecules/user-avatar";
import {
  useClientMembers,
  useCurrentClient,
} from "@/features/clients/hooks/use-clients";
import { UsersIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "@/features/utils/i18n";

interface MembersSelectProps {
  value: string[]; // Array of member IDs
  onChange: (value: string[]) => void;
  allowAll?: boolean; // Add "Everyone" option
  multiple?: boolean; // Enable multi-select with checkboxes
  placeholder?: string;
  disabled?: boolean;
  showBadges?: boolean; // Show badges for selected items
}

export function MembersSelect({
  value,
  onChange,
  allowAll = false,
  multiple = false,
  placeholder,
  disabled = false,
  showBadges = true,
}: MembersSelectProps) {
  const { t } = useTranslation();
  const clientId = useCurrentClient()?.client.id || "";
  const { data: members = [], isLoading } = useClientMembers(clientId);

  // Anybody on the team with a name. It used to also require `m.user`, an
  // attached account — which silently hid every member who does not have one
  // (a performer synced from the booking provider, the reception desk), so
  // they could not be picked as a performer, an attendee or a routing
  // destination. A name is what a picker needs; a login is not.
  const activeMembers = members
    .filter((m) => (m.membership.display_name || m.user?.full_name)?.trim())
    .map((m) => ({
      id: m.membership.id,
      name: m.membership.display_name || m.user?.full_name || m.user?.email || "",
      email: m.user?.email || "",
      avatar: m.user?.preferences?.avatar,
    }));

  // Convert to SearchableSelectItem format
  const items: SearchableSelectItem[] = useMemo(
    () =>
      activeMembers.map((member) => ({
        id: member.id,
        label: member.name,
        description: member.email,
        icon: (
          <UserAvatar
            avatar={member.avatar}
            name={member.name}
            email={member.email}
            size="sm"
          />
        ),
        data: member,
      })),
    [activeMembers],
  );

  return (
    <SearchableSelect
      items={items}
      value={value}
      onChange={(v) => onChange(Array.isArray(v) ? v : [v])}
      multiple={multiple}
      placeholder={placeholder ?? t("common.select.members.placeholder")}
      searchPlaceholder={t("common.select.members.search")}
      emptyText={t("common.select.members.empty")}
      isLoading={isLoading}
      disabled={disabled}
      allowNone={allowAll}
      noneOption={{
        id: "ALL",
        label: t("common.select.members.all"),
        description: t("common.select.members.allDescription"),
        icon: <UsersIcon className="h-4 w-4" />,
      }}
      showBadges={multiple && showBadges !== false}
      showSelectAll={multiple}
    />
  );
}
